import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Compass, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { buildTourSteps, type TourStep } from '@/lib/productTour'
import { onStartTour, tourNav } from './tourBus'

// ── Product tour ────────────────────────────────────────────────────────────
// Mounted once in the admin shell. Started from Settings or the menu under the
// staff member's name (startProductTour), any time; nothing is saved. A card
// at the bottom of the screen walks the sidebar step by step, in the staff
// member's role's words (lib/productTour):
// each step opens its screen and rings its sidebar row (data-tour). The page
// stays usable underneath, so a step can be tried as it is explained.

/** On screen and laid out: the closed mobile drawer's rows are not. */
function onScreen(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight
}

function typingIn(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

export function ProductTour() {
  const navigate = useNavigate()
  const [steps, setSteps] = useState<TourStep[] | null>(null)
  const [index, setIndex] = useState(0)
  const [ring, setRing] = useState<DOMRect | null>(null)
  const nextRef = useRef<HTMLButtonElement>(null)

  useEffect(
    () =>
      onStartTour(() => {
        const { nav, role } = tourNav()
        const built = buildTourSteps(nav, role)
        if (built.length === 0) return
        setSteps(built)
        setIndex(0)
      }),
    [],
  )

  const step = steps?.[index]
  const last = !!steps && index === steps.length - 1
  const close = useCallback(() => {
    setSteps(null)
    setRing(null)
  }, [])
  const next = useCallback(() => setIndex((i) => (steps ? Math.min(i + 1, steps.length - 1) : i)), [steps])
  const back = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), [])

  // Open the step's screen.
  useEffect(() => {
    if (step?.href) void navigate({ to: step.href })
  }, [step, navigate])

  // Ring the step's sidebar row, following resizes and scrolls; none when the
  // row is off screen (a closed drawer, a hidden sidebar).
  useEffect(() => {
    const target = step?.target
    if (!target) {
      setRing(null)
      return
    }
    let raf = 0
    const measure = () => {
      const el = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(target)}"]`)).find(onScreen)
      setRing(el ? el.getBoundingClientRect() : null)
    }
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    schedule()
    // Again once the sidebar has reacted to the navigation (a group opens).
    const settled = window.setTimeout(schedule, 350)
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(settled)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
    }
  }, [step])

  useEffect(() => {
    if (!steps) return
    const onKey = (e: KeyboardEvent) => {
      if (typingIn(e.target)) return
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [steps, close, next, back])

  useEffect(() => {
    if (step) nextRef.current?.focus()
  }, [step])

  if (!steps || !step) return null
  const grouped = step.lines.some((l) => l.label)

  return (
    <>
      {ring && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[80] rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background transition-all duration-200 motion-reduce:transition-none"
          style={{ left: ring.left, top: ring.top, width: ring.width, height: ring.height }}
        />
      )}
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby="product-tour-title"
        className="fixed inset-x-3 bottom-3 z-[80] mx-auto max-w-md rounded-xl border border-border bg-card p-4 text-card-foreground shadow-xl sm:bottom-6"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Compass className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Tour · {index + 1} of {steps.length}
            </p>
            <h2 id="product-tour-title" className="mt-0.5 text-base font-semibold leading-snug">
              {step.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close the tour"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {grouped ? (
          <ul className="mt-3 space-y-1.5 text-sm">
            {step.lines.map((l) => (
              <li key={l.label ?? l.text}>
                <span className="font-medium text-foreground">{l.label}</span>
                <span className="text-muted-foreground"> — {l.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            {step.lines.map((l) => (
              <p key={l.text}>{l.text}</p>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-2">
          {last ? (
            <span />
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={close}>
              Skip tour
            </Button>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={back} disabled={index === 0}>
              <ArrowLeft className="mr-1 h-4 w-4" aria-hidden /> Back
            </Button>
            <Button type="button" size="sm" ref={nextRef} onClick={last ? close : next}>
              {last ? (
                'Done'
              ) : (
                <>
                  Next <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
